import { useLayoutEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import type { PostMedia } from '@dove/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { mediaUrl } from '@/lib/api';
import { useInViewport } from '@/lib/in-viewport';
import { useReducedMotion } from '@/lib/motion';
import { placeholderStyle } from '@/lib/placeholder';
import { cn } from '@/lib/utils';

/**
 * Attachments on an open letter: a GIF (docs/5 § D260805-1220), or up to
 * POST_MAX_IMAGES stills (docs/5 § D260808-2252). Never both, so this file is two
 * shapes with a branch between them rather than one that has to hold both — a GIF
 * is a tile, and photographs are prints laid on the page (`PrintStack`).
 *
 * A GIF PLAYS BY ITSELF WHILE IT IS ON SCREEN (docs/5 § D260805-2116). It used to be a
 * poster with a play control, and that is the rule this file argued for at length before
 * it was reversed: a GIF is a reaction rather than a video, and a reaction that has to be
 * pressed is a reaction nobody presses. Nothing in the tile pauses it, because a control
 * for stopping a two-second loop is furniture.
 *
 * "WHILE IT IS ON SCREEN" IS THE WHOLE OF THE RESTRAINT, and it is load-bearing rather
 * than a nicety. It bounds the bytes — the animation is fetched when the tile arrives,
 * not when the feed does, so scrolling past ten letters costs ten posters and not ten
 * animations — and it bounds the motion, so nothing is looping in a part of the page
 * nobody is looking at.
 *
 * `prefers-reduced-motion` IS STILL HONOURED, AND IT IS NOW THE ONLY OFF-SWITCH. A
 * reader who has asked their OS for stillness gets the poster and a play control, which
 * is what this file used to give everybody. That is deliberate: autoplay is a decision
 * about the default, not a decision that somebody who cannot tolerate motion should be
 * shown motion anyway.
 *
 * WHERE THERE IS NO STILL FRAME the provider had none to give. Under reduced motion the
 * tile draws its description on a muted ground instead — the animation is not a fallback
 * for a reader who asked for stillness.
 *
 * EVERY ATTACHMENT MAY CARRY A DESCRIPTION, AND A GIF'S IS ANNOUNCED HERE. The ALT
 * chip on a GIF's tile is visible to sighted readers too, which is the arrangement
 * that has ever reliably made people write descriptions.
 *
 * A PRINT CARRIES NO CHIP (docs/5 § D260809-0041), and the argument for why the two
 * disagree is written where the chip used to be. In short: the pressure that
 * produces descriptions applies in the COMPOSER, on your own draft, and three
 * little labels on three overlapping photographs are three pieces of furniture on
 * the one surface here meant to look like objects rather than like a feed.
 */

/** X-style mosaics, for a GIF. Stills are not drawn in a grid at all — see
 *  `PrintStack`. Kept as a map because a GIF can only ever be one of them and a
 *  map with one entry reads as a mistake. */
const GRID: Record<number, string> = {
  1: 'grid-cols-1',
};

function clampAspect(a: number) {
  /* Between a tall portrait and a wide panorama, so one attachment can never
     take over the viewport or collapse to a strip. */
  return Math.min(1.91, Math.max(0.8, a));
}

function Tile({ media, only }: { media: PostMedia; only: boolean }) {
  /*
   * THE PREFERENCE IS READ HERE NOW, which it deliberately was not: while a letter's GIF
   * was a poster for everybody there was no branch a preference could change, and the
   * check could only ever agree. Autoplay creates the branch.
   */
  const reduced = useReducedMotion();
  const { ref, visible } = useInViewport<HTMLDivElement>();

  /* Only meaningful under reduced motion, where it is what the play control sets — the
     one reader who still has a play control is the one who asked for stillness. */
  const [pressedPlay, setPressedPlay] = useState(false);

  const isGif = media.kind === 'gif';
  const animate = isGif && (reduced ? pressedPlay : visible);
  /* The control exists only for the reader whose default is stillness. Everybody else
     gets an uninterrupted tile: no scrim, no disc, nothing between them and the letter. */
  const showControls = isGif && reduced;

  return (
    <div
      ref={ref}
      className="relative isolate overflow-hidden bg-muted"
      style={only ? { aspectRatio: clampAspect(media.aspect) } : undefined}
    >
      {media.url ? (
        <Frame media={media} animate={animate} />
      ) : (
        /* NO URL MEANS NO PIPELINE YET — a still, which nothing can create today.
           The deterministic gradient is the placeholder the feed has always drawn
           for one (lib/placeholder.ts). */
        <div
          role="img"
          aria-label={media.alt}
          className="size-full"
          style={placeholderStyle(media.seed ?? media.kind, media.kind)}
        />
      )}

      {showControls && !pressedPlay && (
        <button
          type="button"
          aria-label={media.alt ? `Play GIF: ${media.alt}` : 'Play GIF'}
          onClick={() => setPressedPlay(true)}
          className="absolute inset-0 grid place-items-center bg-black/15 transition-colors hover:bg-black/25 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
        >
          <span className="grid size-11 place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm">
            <Play aria-hidden className="size-5 translate-x-px fill-current" />
          </span>
        </button>
      )}

      {/* AND IT CAN BE STOPPED AGAIN, in the same place it was started — but only for
          this reader. Somebody who asked their OS for stillness and then chose to play
          one GIF is exactly the person who needs to be able to put it back; a reader who
          never asked for stillness is not shown a stop control for a two-second loop
          (docs/5 § D260805-2116). */}
      {showControls && pressedPlay && (
        <button
          type="button"
          aria-label="Stop"
          onClick={() => setPressedPlay(false)}
          className="absolute inset-0 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
        />
      )}

      <div className="pointer-events-none absolute bottom-1.5 start-1.5 flex gap-1">
        {media.kind === 'gif' && <Chip>GIF</Chip>}
        {media.alt.trim() && <Chip>ALT</Chip>}
      </div>
    </div>
  );
}

/**
 * The picture itself.
 *
 * THE POSTER AND THE ANIMATION ARE ONE `<img>` WITH TWO SOURCES, so starting one
 * does not lay the tile out again — the box is already the right shape, and swapping
 * `src` inside it keeps the letter's height fixed while the GIF loads.
 *
 * IT SWAPS BACK WHEN THE TILE LEAVES THE VIEWPORT, which is what "plays while it is on
 * screen" means in practice (docs/5 § D260805-2116) — and it costs nothing to return to,
 * because both URLs are in the browser's cache by then. The loop restarts from its first
 * frame on the way back, which is the honest reading of a GIF that was not being watched.
 */
function Frame({ media, animate }: { media: PostMedia; animate: boolean }) {
  /* THE BYTES ARE NOT OURS, so a GIF withdrawn upstream is a state this has to draw
     (docs/5 § D260805-1220) — as the letter's own description rather than as the
     browser's broken-image glyph in the middle of somebody's writing. */
  const [broken, setBroken] = useState(false);
  const still = media.stillUrl;
  const src = animate || !still ? media.url : still;

  /*
   * NO STILL AND NOT ANIMATING: the description, on the muted ground the tile already
   * has. For most readers this is a moment on the way into the viewport; for a reader
   * who asked for stillness it is the resting state, and it is better than the
   * alternative, which is motion they told their OS they cannot have.
   *
   * AT THE TOP AND CLAMPED TO TWO LINES, which is a fix rather than a preference: under
   * reduced motion the play control is centred in this tile, so a centred description
   * sits underneath a 44px disc with a triangle in it and the two read as one damaged
   * thing. Held to the top edge, the caption is a caption and the control is a control.
   */
  if (!src || broken || (!animate && !still)) {
    return (
      <span className="absolute inset-x-0 top-0 line-clamp-2 p-2.5 text-start text-[13px]/[1.4] text-muted-foreground">
        {media.alt || 'A GIF'}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={media.alt}
      loading="lazy"
      decoding="async"
      onError={() => setBroken(true)}
      className="size-full object-cover"
    />
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-black/60 px-1 py-px text-[10px] leading-4 font-semibold tracking-wide text-white backdrop-blur-sm">
      {children}
    </span>
  );
}

/**
 * HOW THE PRINTS LIE (docs/5 § D260808-2252).
 *
 * A PHOTOGRAPH IS NOT A TILE. The mosaic that used to be here is what a feed
 * built for volume does — flush edges, a 16:9 box, four cells in a grid — and it
 * makes a set of photographs read as a UNIT of content. This product is
 * correspondence: three photographs in a letter are three things somebody put in
 * an envelope, so they are drawn as prints laid on the page, tilted, overlapping
 * the way a small pile of prints actually sits.
 *
 * THE NUMBERS ARE SMALL ON PURPOSE. Four degrees reads as "placed by hand"; ten
 * reads as a broken layout, and on a phone column it costs real width to the
 * corners sticking out. The stagger is a few pixels of vertical offset, which is
 * what stops three tilted prints from reading as one wavy strip.
 *
 * EACH ONE IS ROTATED LESS THAN THE LAST IS NOT A RULE HERE — the angles
 * alternate in sign, because a pile whose prints all lean the same way looks like
 * a layout bug rather than a pile.
 *
 * `width` AND `overlap` ARE PERCENTAGES THAT MUST NOT ADD UP TO MORE THAN 100.
 * They are checked by eye and by the overflow assertion the review harness runs
 * (CLAUDE.md): `n × width − (n − 1) × overlap` is the pile's own width, and the
 * container's padding is what absorbs the corners that rotation pushes out.
 */
const PILE: Record<number, { rotate: number; lift: number }[]> = {
  /* A lone print leans least, and it is still meant to be NOTICEABLE — at 1.2°
     it read as a straight photograph with a rendering fault rather than as one
     laid down by hand, which is the failure mode a small angle has. */
  1: [{ rotate: -1.7, lift: 0 }],
  2: [
    { rotate: -3, lift: 3 },
    { rotate: 2.6, lift: -3 },
  ],
  3: [
    { rotate: -4, lift: 6 },
    { rotate: 1.4, lift: -4 },
    { rotate: 4, lift: 5 },
  ],
};

/** How wide each print is, and how far the next one lies over it, as
 *  percentages of the column. */
const PILE_WIDTH: Record<number, { width: number; overlap: number }> = {
  1: { width: 100, overlap: 0 },
  2: { width: 53, overlap: 8 },
  3: { width: 38, overlap: 9 },
};

/**
 * The photographs on a letter, laid out as prints.
 *
 * ONE PRINT KEEPS ITS OWN SHAPE and two or three are SQUARED. A lone photograph
 * is the letter's subject, so it is drawn at its own proportions (clamped, so a
 * panorama cannot become a strip and a tall portrait cannot take the whole
 * screen). A pile of them is a set, and a set of mixed orientations laid at
 * angles is a mess — the square is the crop that treats a landscape and a
 * portrait alike, and nothing is lost, because tapping one opens the whole
 * photograph (`Viewer`).
 */
function PrintStack({ stills, className }: { stills: PostMedia[]; className?: string }) {
  const [open, setOpen] = useState<number | null>(null);
  const count = Math.min(stills.length, 3);
  const lay = PILE[count] ?? PILE[3]!;
  const { width, overlap } = PILE_WIDTH[count] ?? PILE_WIDTH[3]!;

  return (
    <>
      <div
        /*
         * THE TILT MIRRORS IN RTL, which is what `--tilt` is for. A pile leaning
         * one way is a hand's gesture, and a hand's gesture on a right-to-left
         * page goes the other way — the same reason every chevron in this app
         * carries `rtl:-scale-x-100`. It cannot be a `scale-x` here, because that
         * would mirror the photographs themselves.
         *
         * THE PADDING IS STRUCTURAL. A rotated box is wider and taller than its
         * own layout size, and without room to lean into it the corners are
         * clipped by `overflow` or push the column sideways — which is the
         * horizontal-overflow bug the review harness asserts against.
         */
        className={cn(
          'flex items-center justify-center px-2 py-2 [--tilt:1] rtl:[--tilt:-1]',
          className,
        )}
      >
        {stills.slice(0, 3).map((still, at) => {
          const { rotate, lift } = lay[at]!;
          return (
            <button
              key={still.id ?? at}
              type="button"
              onClick={() => setOpen(at)}
              aria-label={still.alt ? `Open: ${still.alt}` : 'Open this photograph'}
              className={cn(
                'relative isolate shrink-0 overflow-hidden rounded-xl border border-border bg-muted shadow-md transition-transform',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                count === 1 ? '' : 'aspect-square',
              )}
              style={{
                width: `${width}%`,
                marginInlineStart: at === 0 ? undefined : `-${overlap}%`,
                /* EARLIER PRINTS LIE ON TOP, which is what puts each print's END
                   edge above its neighbour — and therefore keeps every ALT chip
                   visible, since they sit in that corner. */
                zIndex: 10 - at,
                rotate: `calc(var(--tilt) * ${rotate}deg)`,
                translate: `0 ${lift}px`,
                ...(count === 1
                  ? { aspectRatio: clampAspect(still.aspect) }
                  : undefined),
              }}
            >
              {still.id && (
                <img
                  src={mediaUrl(still.id)}
                  alt={still.alt}
                  loading="lazy"
                  decoding="async"
                  className="size-full object-cover"
                />
              )}
              {/*
                NO ALT CHIP ON A PRINT (docs/5 § D260809-0041). A GIF's tile still
                carries one and should: it is a single flat tile with a corner to
                spare. A pile is three overlapping photographs at an angle, and
                three little black labels on it are three pieces of furniture on
                the one surface in this product that is meant to look like objects
                on a page rather than like a feed.

                WHAT THE CHIP WAS FOR IS NOT LOST. Its job is to make descriptions
                visible so that people write them, and the surface where that
                pressure applies is the COMPOSER, where `+ ALT` sits on your own
                draft in the attention colour until you write one — which since
                § D260809-0019 is the whole of the ask. On a reader's screen the
                chip only ever announced that somebody else had done it; the
                description itself is one tap away, under the photograph.
              */}
            </button>
          );
        })}
      </div>

      <Viewer stills={stills} at={open} onClose={() => setOpen(null)} />
    </>
  );
}

/**
 * The photographs, whole, one at a time — dragged between (docs/5 § D260809-0041).
 *
 * IT EXISTS BECAUSE THE PILE CROPS. Two or three prints are squared so they read
 * as a set, which means a landscape photograph is showing its middle — and a
 * reader who cannot get to the rest of it is looking at an attachment the sender
 * did not send. `object-contain` here is the other half of `object-cover` there.
 *
 * IT OPENS ON THE ONE THAT WAS TAPPED AND CARRIES THE REST WITH IT. Opening a
 * pile of three at the third photograph and offering no way to the first two is
 * a viewer that makes the reader close it and aim again — three times, at
 * overlapping targets the size of a thumbnail.
 *
 * THE GESTURE IS A SCROLL, NOT A GESTURE LIBRARY. `scroll-snap-type: x mandatory`
 * with one full-width slide per photograph is what makes a drag land on a
 * photograph rather than between two — and because it is the browser's own
 * scrolling, it arrives with momentum, rubber-banding at the ends, and the
 * flick-versus-drag distinction already correct on every device. A JavaScript
 * pan handler reimplements all of that and gets the last one wrong.
 *
 * A MOUSE IS THE ONE CASE THAT NEEDS CODE, because a mouse cannot drag a scroll
 * container. `onPointerDown` maps its movement onto `scrollLeft` and lets go on
 * release, so snapping settles it — and it is scoped to `pointerType === 'mouse'`
 * so it can never fight touch, which is already correct without it.
 *
 * THE APP'S ONE DIALOG SHAPE, which is a sheet (`ui/dialog`). A full-bleed black
 * lightbox would be a second modal idiom for one surface, and this product has
 * exactly one: things come up from the bottom edge and go back down.
 *
 * THE DESCRIPTION IS UNDER IT, in words rather than as a chip, and it changes
 * with the photograph. This is the one surface with room for the sentence
 * somebody wrote, and it is what a reader who opened a photograph they cannot
 * make out is looking for.
 */
function Viewer({
  stills,
  at,
  onClose,
}: {
  stills: PostMedia[];
  at: number | null;
  onClose: () => void;
}) {
  const strip = useRef<HTMLDivElement>(null);
  const [showing, setShowing] = useState(at ?? 0);
  const open = at !== null;

  /*
   * OPEN ON THE ONE THAT WAS TAPPED, WITHOUT SCROLLING THERE.
   *
   * `behavior: 'instant'` because the alternative is a viewer that opens on the
   * first photograph and then slides to the third in front of the reader, which
   * reads as the app changing its mind. The layout effect runs before paint, so
   * the first frame is already the right photograph.
   *
   * IT WAITS FOR A WIDTH. Radix mounts the sheet's content when it opens, and on
   * that first pass the element can still measure zero — multiplying an index by
   * zero silently lands every photograph on the first one. The frame callback is
   * the retry.
   */
  useLayoutEffect(() => {
    if (!open) return;
    setShowing(at);

    let cancelled = false;
    let tries = 0;
    const place = () => {
      if (cancelled) return;
      /*
       * THE ELEMENT IS RE-READ EVERY ATTEMPT, and that is the fix rather than a
       * flourish. Radix mounts a dialog's content into a portal on a LATER commit
       * than the one that flips it open, so on this first pass `strip.current` is
       * still null — an early return here meant the placement never happened at
       * all and every photograph opened on the first one, however many times the
       * width was checked. Caught by sampling `scrollLeft` across the frames
       * after the tap: it was 0 from the first frame the element existed, which
       * is what "never ran" looks like and "ran and was overridden" does not.
       *
       * A ZERO WIDTH IS THE SECOND WAY THIS ARRIVES EARLY, and multiplying an
       * index by it lands everything on the first slide just as quietly.
       */
      const el = strip.current;
      if (!el || !el.clientWidth) {
        /* Bounded, because an unbounded rAF chain outlives a sheet that is
           closed again before it ever measured. Half a second of frames is far
           past a portal mounting and far short of anything a person notices. */
        if (tries++ < 30) requestAnimationFrame(place);
        return;
      }
      /* NEGATIVE ON AN RTL PAGE. `scrollLeft` is measured from the inline START,
         so on a right-to-left layout the second slide is at MINUS one width —
         which is also why `onScroll` reads an absolute value. Without the sign
         this clamps to 0 and every photograph opens on the first one. */
      const sign = getComputedStyle(el).direction === 'rtl' ? -1 : 1;
      el.scrollTo({ left: sign * at * el.clientWidth, behavior: 'instant' });
    };
    place();
    return () => {
      cancelled = true;
    };
  }, [open, at]);

  /* WHICH ONE IS IN FRONT, read from the scroll position rather than tracked as
     state the gesture has to keep in sync. The scroll IS the state; this only
     reports it, so a drag, a flick, an arrow key and the effect above cannot
     disagree about where the strip is. */
  const onScroll = () => {
    const el = strip.current;
    if (!el || !el.clientWidth) return;
    const next = Math.round(Math.abs(el.scrollLeft) / el.clientWidth);
    setShowing((now) => (now === next ? now : next));
  };

  /*
   * Arrow keys, because a sheet that can be dragged should also be steppable by
   * somebody who is not holding the screen. It scrolls BY a screen rather than TO
   * an index, which is what keeps the strip the single source of truth
   * (see `onScroll`) — and it is also the version that needs no RTL branch: a
   * positive `left` moves the content visually rightwards whichever way the page
   * runs, so ArrowRight means "the way the arrow points" on both.
   */
  const step = (towards: number) => {
    const el = strip.current;
    if (!el) return;
    el.scrollBy({ left: towards * el.clientWidth, behavior: 'smooth' });
  };

  /* A MOUSE DRAG, AND ONLY A MOUSE DRAG. Touch and trackpad already scroll this
     element natively; intercepting them would replace something that works with
     something that has to be maintained. */
  const dragFrom = useRef<{ x: number; left: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') return;
    const el = strip.current;
    if (!el) return;
    dragFrom.current = { x: e.clientX, left: el.scrollLeft };
    el.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const from = dragFrom.current;
    const el = strip.current;
    if (!from || !el) return;
    el.scrollLeft = from.left - (e.clientX - from.x);
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragFrom.current) return;
    dragFrom.current = null;
    strip.current?.releasePointerCapture(e.pointerId);
  };

  const many = stills.length > 1;
  const current = stills[showing];

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* `pt-9` SO THE CLOSE CONTROL IS NOT STANDING ON THE PHOTOGRAPH. The sheet
          puts its X at the top corner, absolutely, and every other sheet in the
          app has a heading there for it to sit beside; this one's content starts
          at the top edge. Caught by looking at it.

          `px-0` SO A SLIDE IS THE FULL WIDTH OF THE SHEET, which is what makes the
          snap land on an edge rather than on a gutter. The padding that would have
          been here belongs to the caption below instead. */}
      <DialogContent
        className="px-0 pt-9 pb-4"
        onKeyDown={(e) => {
          if (!many) return;
          if (e.key === 'ArrowRight') step(1);
          if (e.key === 'ArrowLeft') step(-1);
        }}
      >
        {/* Required by Radix and read only by a screen reader: the sheet's own
            name. The photograph's description is the paragraph below, which is
            the thing with something to say. */}
        <DialogTitle className="sr-only">
          {many ? 'Photographs' : 'Photograph'}
        </DialogTitle>

        <div
          ref={strip}
          onScroll={onScroll}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          /* `snap-mandatory` rather than `proximity`: a photograph half on screen
             is not a state this viewer has, so every release has to land on one.
             `overscroll-x-contain` keeps a flick at the last photograph from
             turning into the browser's back gesture. */
          className={cn(
            /* `mt-2` SO THE PHOTOGRAPH IS NOT TOUCHING THE GRABBER. The sheet
               draws its grab handle inside the same padded box, and a tall
               portrait fills its slide to the pixel — so without this the top
               edge of somebody's photograph and the handle are two or three
               pixels apart and read as one damaged object. */
            'mt-2 flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain',
            /* The scrollbar is furniture on a strip of three. */
            '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            many && 'cursor-grab active:cursor-grabbing',
          )}
        >
          {stills.map((still, i) => (
            <div
              key={still.id ?? i}
              className="flex w-full shrink-0 snap-center items-center justify-center px-4"
            >
              {still.id && (
                <img
                  src={mediaUrl(still.id)}
                  alt={still.alt}
                  /* NOT DRAGGABLE, which is a real fix rather than a nicety: a
                     mouse drag on an image is the browser's own image-drag
                     gesture, and it swallows the pointer move this strip is
                     listening for. */
                  draggable={false}
                  /* THE STRIP IS AS TALL AS THE TALLEST PHOTOGRAPH IN IT, which is
                     what stops the sheet growing and shrinking as somebody drags
                     between a portrait and a panorama. The cost is air around the
                     short ones, and it is the right side of that trade: a viewer
                     that resizes under the thumb is a viewer that is hard to
                     drag. 58svh leaves room for the handle above and the dots
                     below without the sheet reaching for the whole screen. */
                  className="max-h-[58svh] w-auto max-w-full rounded-lg object-contain select-none"
                />
              )}
            </div>
          ))}
        </div>

        {/* WHERE YOU ARE IN THE PILE, as dots rather than as "2 of 3". A number is
            the wrong register for three photographs — it counts something at the
            reader, and this product does not put numbers on things
            (docs/1 § No vanity metrics is about tallies, and the habit is general).
            Dots say the same thing at a glance and disappear when there is one. */}
        {many && (
          <div aria-hidden className="mt-3 flex justify-center gap-1.5">
            {stills.map((still, i) => (
              <span
                key={still.id ?? i}
                className={cn(
                  'size-1.5 rounded-full transition-colors',
                  i === showing ? 'bg-foreground' : 'bg-muted-foreground/35',
                )}
              />
            ))}
          </div>
        )}

        {/* THE HEIGHT IS HELD WHETHER OR NOT THERE IS A DESCRIPTION, so dragging
            between a described photograph and an undescribed one does not make the
            sheet grow and shrink under the thumb. Since D260809-0019 an empty one
            is ordinary, so this is the common case rather than the edge. */}
        <div className="min-h-9 px-4">
          {current?.alt.trim() && (
            <DialogDescription className="mt-3">{current.alt}</DialogDescription>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PostMediaGrid({
  media,
  className,
}: {
  media: PostMedia[];
  className?: string;
}) {
  if (!media.length) return null;

  /* TWO SHAPES, AND THE ATTACHMENT DECIDES WHICH (docs/5 § D260808-2252). A letter
     carries a GIF or photographs and never both — the server refuses a mixture —
     so this is a branch rather than a merge, and the GIF keeps the tile it has
     always had. */
  const stills = media.filter((m) => m.kind === 'image' && m.id);
  if (stills.length) return <PrintStack stills={stills} className={className} />;

  const items = media.slice(0, 1);

  return (
    <div
      className={cn(
        'grid gap-0.5 overflow-hidden rounded-xl border border-border',
        GRID[items.length],
        className,
      )}
    >
      {items.map((m, i) => (
        /* KEYED ON WHAT THE ATTACHMENT IS: the provider's id for a GIF, the
           placeholder seed for a scaffold still, and the index for neither — a key
           is only load-bearing where a list can be reordered, and this one cannot. */
        <Tile key={m.id ?? m.seed ?? i} media={m} only={items.length === 1} />
      ))}
    </div>
  );
}
